import { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { computePayroll } from '../../lib/payrollCalculator';
import { logAudit } from '../../lib/audit';

const DEFAULT_SMMLV_2026 = 1750905;
const DEFAULT_AUX_TRANSPORTE_2026 = 249095;

const PayrollConfigView = ({ onBack, currentUser }) => {
  const [config, setConfig] = useState({
    enabled: true,
    smmlv: DEFAULT_SMMLV_2026,
    auxTransporte: DEFAULT_AUX_TRANSPORTE_2026,
    saludEmp: 0.04,
    pensionEmp: 0.04,
    fsp: { enabled: true, thresholdMultiples: 4, rate: 0.01 },
    ibcMinMultiples: 1,
    ibcMaxMultiples: 25,
    employer: { salud: 0.085, pension: 0.12, arl: 0.00522, ccf: 0.04, sena: 0.02, icbf: 0.03, cesantias: 0.0833, interesesCes: 0.01, prima: 0.0833 },
    dailyDefaultRates: { weekdays: 0, saturday: 0, sunday: 0 },
  });
  const [sampleSalary, setSampleSalary] = useState(DEFAULT_SMMLV_2026);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const ref = doc(db, 'payrollSettings', 'global');
        const snap = await getDoc(ref);
        if (snap.exists()) setConfig({ ...config, ...snap.data() });
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const handleChangePct = (name) => (e) => {
    let v = parseFloat(e.target.value);
    if (isNaN(v)) v = 0;
    v = Math.max(0, Math.min(100, v));
    setConfig((prev) => ({ ...prev, [name]: v / 100 }));
  };

  const handleChangeNum = (name) => (e) => {
    let v = parseFloat(e.target.value);
    if (isNaN(v)) v = 0;
    setConfig((prev) => ({ ...prev, [name]: v }));
  };

  const handleToggle = (name) => (e) => {
    const v = e.target.checked;
    setConfig((prev) => ({ ...prev, [name]: v }));
  };

  const sample = computePayroll({
    settings: config,
    employee: { baseSalary: Number(sampleSalary) || 0 },
    period: { extras: 0, bonuses: 0 },
  });

  const handleSave = async () => {
    try {
      await setDoc(doc(db, 'payrollSettings', 'global'), config, { merge: true });
      alert('Configuración de nómina guardada');
      // Auditoría
      try {
        await logAudit({
          module: 'Nómina',
          action: 'payroll.settings.saved',
          severity: 'info',
          details: { ...config, _sample: sample?.neto ?? null },
        });
      } catch {}
    } catch (e) {
      alert('No se pudo guardar configuración');
      console.error(e);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-6 sm:mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400 w-full md:w-auto justify-center md:justify-start"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-900 drop-shadow-sm text-center md:text-left">Configuración de Nómina</h1>
        <div className="hidden md:block w-32"></div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 w-full max-w-4xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center">Parámetros de Nómina (Colombia)</h2>
        {loading && <p className="text-sm text-gray-500 mb-4">Cargando…</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usar estos parámetros</label>
            <input type="checkbox" checked={!!config.enabled} onChange={handleToggle('enabled')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SMMLV</label>
            <input type="number" value={config.smmlv} onChange={handleChangeNum('smmlv')} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Auxilio de Transporte</label>
            <input type="number" value={config.auxTransporte} onChange={handleChangeNum('auxTransporte')} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salud empleado (%)</label>
            <input type="number" value={(config.saludEmp*100).toFixed(2)} onChange={handleChangePct('saludEmp')} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pensión empleado (%)</label>
            <input type="number" value={(config.pensionEmp*100).toFixed(2)} onChange={handleChangePct('pensionEmp')} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">FSP activo</label>
              <input type="checkbox" checked={!!config.fsp?.enabled} onChange={(e)=>setConfig(prev=>({ ...prev, fsp: { ...(prev.fsp||{}), enabled: e.target.checked } }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">FSP umbral (×SMMLV)</label>
              <input type="number" value={config.fsp?.thresholdMultiples||4} onChange={(e)=>setConfig(prev=>({ ...prev, fsp: { ...(prev.fsp||{}), thresholdMultiples: Number(e.target.value)||4 } }))} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">FSP tasa (%)</label>
              <input type="number" value={((config.fsp?.rate||0)*100).toFixed(2)} onChange={(e)=>setConfig(prev=>({ ...prev, fsp: { ...(prev.fsp||{}), rate: (Number(e.target.value)||0)/100 } }))} className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IBC mínimo (×SMMLV)</label>
            <input type="number" value={config.ibcMinMultiples} onChange={handleChangeNum('ibcMinMultiples')} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IBC máximo (×SMMLV)</label>
            <input type="number" value={config.ibcMaxMultiples} onChange={handleChangeNum('ibcMaxMultiples')} className="w-full px-4 py-2 border rounded-lg" />
          </div>
        </div>

        <div className="mt-8 border border-gray-200 rounded-xl p-4 bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Tarifas diarias por defecto</h3>
          <p className="text-sm text-gray-500 mb-4">
            Estas tarifas se usarán como valores iniciales cuando registres un empleado nuevo (y al elegir “usar configuración”).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lunes a Viernes</label>
              <input
                type="number"
                min="0"
                value={config.dailyDefaultRates?.weekdays ?? 0}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    dailyDefaultRates: { ...(prev.dailyDefaultRates || {}), weekdays: Number(e.target.value || 0) },
                  }))
                }
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sábado</label>
              <input
                type="number"
                min="0"
                value={config.dailyDefaultRates?.saturday ?? 0}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    dailyDefaultRates: { ...(prev.dailyDefaultRates || {}), saturday: Number(e.target.value || 0) },
                  }))
                }
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domingo / Festivo</label>
              <input
                type="number"
                min="0"
                value={config.dailyDefaultRates?.sunday ?? 0}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    dailyDefaultRates: { ...(prev.dailyDefaultRates || {}), sunday: Number(e.target.value || 0) },
                  }))
                }
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-8 border-t pt-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Vista Previa (ejemplo con salario base)</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Salario Base de Ejemplo</label>
            <input
              type="number"
              value={sampleSalary}
              onChange={(e) => setSampleSalary(parseFloat(e.target.value) || 0)}
              min="0"
              step="1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
          <div className="bg-gray-50 p-4 rounded-lg shadow-inner space-y-1">
            <p className="flex justify-between text-lg"><span>Salario Base:</span> <strong>${(sample.employee?.baseSalary || sampleSalary).toFixed?.(2) || sampleSalary.toFixed(2)}</strong></p>
            <p className="flex justify-between text-sm"><span>Auxilio Transporte:</span> <span>${sample?.devengados?.auxTransporte?.toFixed?.(2) || '0.00'}</span></p>
            <p className="flex justify-between text-sm"><span>IBC:</span> <span>${sample?.ibc?.toFixed?.(2) || '0.00'}</span></p>
            <p className="flex justify-between text-sm text-red-600"><span>Salud (4%):</span> <span>-${sample?.deductions?.salud?.toFixed?.(2) || '0.00'}</span></p>
            <p className="flex justify-between text-sm text-red-600"><span>Pensión (4%):</span> <span>-${sample?.deductions?.pension?.toFixed?.(2) || '0.00'}</span></p>
            {config?.fsp?.enabled && <p className="flex justify-between text-sm text-red-600"><span>FSP:</span> <span>-${sample?.deductions?.fsp?.toFixed?.(2) || '0.00'}</span></p>}
            <p className="flex justify-between text-2xl font-bold text-green-700 border-t pt-2"><span>Salario Neto Estimado:</span> <span>${sample?.neto?.toFixed?.(2) || '0.00'}</span></p>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200"
          >
            Guardar Configuración
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayrollConfigView;

